import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, Plus, Edit2, UserX, Search, Settings2 } from "lucide-react";
import { ADMIN } from "@/constants/testIds";
import { useDepartments } from "@/hooks/useDepartments";

import { API } from "../lib/api";
const ax = () => axios.create({ withCredentials: true });
const ROLES = { admin: "Administrador", area_manager: "Gerente de Área", regional_manager: "Gerente Regional", store_manager: "Gerente de Loja", coordinator: "Coordenador/Supervisor", analyst: "Analista/Assistente", operational: "Vendedor/Operacional" };
const ROLE_COLORS = { admin: "bg-red-100 text-red-700", area_manager: "bg-purple-100 text-purple-700", regional_manager: "bg-indigo-100 text-indigo-700", store_manager: "bg-blue-100 text-blue-700", coordinator: "bg-cyan-100 text-cyan-700", analyst: "bg-green-100 text-green-700", operational: "bg-slate-100 text-slate-600" };

function formatDate(dt) { try { return new Date(dt).toLocaleDateString("pt-BR"); } catch { return dt; } }

export default function AdminPage() {
  const { user } = useAuth();
  const { departments } = useDepartments();
  const DEPTS = departments;
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "operational", department: "", phone: "", birth_date: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submissions, setSubmissions] = useState([]);

  const load = async () => {
    try {
      const [ur, sr] = await Promise.all([
        ax().get(`${API}/users`),
        ax().get(`${API}/form-submissions`)
      ]);
      setUsers(ur.data);
      setSubmissions(sr.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.department?.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => { setEditing(null); setForm({ name: "", email: "", password: "", role: "operational", department: "", phone: "", birth_date: "" }); setShowForm(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, password: "", role: u.role, department: u.department || "", phone: u.phone || "", birth_date: u.birth_date || "" }); setShowForm(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await ax().put(`${API}/users/${editing.user_id}`, payload);
      } else {
        await ax().post(`${API}/users`, form);
      }
      setShowForm(false); load();
    } catch (e) { console.error(e.response?.data); }
    setSaving(false);
  };

  const deactivate = async (uid) => {
    if (!window.confirm("Desativar este usuário?")) return;
    await ax().delete(`${API}/users/${uid}`);
    load();
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-2 mb-5">
        <Settings2 className="w-5 h-5 text-[#2346ff]" />
        <div>
          <h1 className="text-xl font-bold font-heading text-slate-900">Painel Administrativo</h1>
          <p className="text-sm text-slate-500">Gestão de usuários e conteúdo</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[
          { label: "Total Usuários", value: users.length, color: "#2346ff" },
          { label: "Ativos", value: users.filter(u => u.is_active).length, color: "#10b981" },
          { label: "Solicitações", value: submissions.length, color: "#f59e0b" },
          { label: "Pendentes", value: submissions.filter(s => s.status === "pending").length, color: "#ef4444" }
        ].map(s => (
          <div key={s.label} className="bento-card p-4">
            <p className="text-2xl font-bold font-heading" style={{ color: s.color }}>{s.value}</p>
            <p className="text-xs text-slate-500 font-medium mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="users">
        <TabsList className="mb-4">
          <TabsTrigger value="users">Usuários</TabsTrigger>
          <TabsTrigger value="submissions">Solicitações de Formulários</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Buscar usuários..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Button data-testid={ADMIN.addUserBtn} onClick={openCreate} style={{ backgroundColor: "#2346ff" }} className="text-white gap-2">
              <Plus className="w-4 h-4" /> Novo Usuário
            </Button>
          </div>

          {loading ? (
            <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />)}</div>
          ) : (
            <div className="bento-card overflow-hidden">
              <Table data-testid={ADMIN.userTable}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>E-mail</TableHead>
                    <TableHead>Perfil</TableHead>
                    <TableHead>Departamento</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(u => (
                    <TableRow key={u.user_id} className={!u.is_active ? "opacity-50" : ""}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-slate-500 text-sm">{u.email}</TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${ROLE_COLORS[u.role] || ROLE_COLORS.operational}`}>
                          {ROLES[u.role] || u.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{u.department || "—"}</TableCell>
                      <TableCell>
                        <Badge className={u.is_active ? "bg-green-100 text-green-700 text-xs" : "bg-red-100 text-red-700 text-xs"}>
                          {u.is_active ? "Ativo" : "Inativo"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button data-testid={ADMIN.editUserBtn} variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(u)} disabled={u.user_id === user?.user_id}>
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          {u.is_active && u.user_id !== user?.user_id && (
                            <Button data-testid={ADMIN.deactivateUserBtn} variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deactivate(u.user_id)}>
                              <UserX className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="submissions">
          <div className="bento-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Formulário</TableHead>
                  <TableHead>Solicitante</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submissions.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8">Nenhuma solicitação</TableCell></TableRow>
                )}
                {submissions.map(s => (
                  <TableRow key={s.submission_id}>
                    <TableCell className="font-medium text-sm">{s.form_id}</TableCell>
                    <TableCell className="text-sm text-slate-600">{s.submitted_by_name}</TableCell>
                    <TableCell className="text-sm text-slate-500">{formatDate(s.created_at)}</TableCell>
                    <TableCell>
                      <Badge className={s.status === "pending" ? "bg-amber-100 text-amber-700 text-xs" : "bg-green-100 text-green-700 text-xs"}>
                        {s.status === "pending" ? "Pendente" : "Processado"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar Usuário" : "Novo Usuário"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome *</Label><Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} /></div>
              <div><Label>E-mail *</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} disabled={!!editing} /></div>
            </div>
            {!editing && <div><Label>Senha *</Label><Input type="password" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} /></div>}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Perfil *</Label>
                <Select value={form.role} onValueChange={v => setForm(f => ({...f, role: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ROLES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Departamento</Label>
                <Select value={form.department || "__none"} onValueChange={v => setForm(f => ({...f, department: v === "__none" ? "" : v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="__none">—</SelectItem>{DEPTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Telefone</Label><Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} /></div>
              <div><Label>Data de Nascimento</Label><Input type="date" value={form.birth_date} onChange={e => setForm(f => ({...f, birth_date: e.target.value}))} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.name || (!editing && !form.password)} style={{ backgroundColor: "#2346ff" }} className="text-white">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
